import React, { useMemo, useRef, useState } from "react";
import { BookOpen, Diamond, Feather, Heart, Lightbulb, MessageSquarePlus, Quote, Scale, Waves } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { CtaButtonDropdown, DropdownItem } from "@/main/react/buttons/CtaButton";
import type { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { ContextualAccountAccessDialogContext } from "@/main/react/account_access/AccountAccessDialogContext";
import { ClientFactory } from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { useViewerActionRecord } from "@/main/common/ViewerActionRecords";
import {
    ENDORSEMENT_KINDS,
    kindsOfRecord,
    readEndorsementRefusalCode,
    toEndorsementRecord,
    type EndorsementKind,
    type EndorsementRefusalCode,
    type ThoughtEndorsementRecord,
    type ThoughtEndorsementSummary,
} from "@/main/common/EndorsementTypes";
import { EndorsementComposer } from "@/main/react/thoughts/EndorsementComposer";
import logger from "@/lib/logger";

export const ENDORSEMENT_KIND_LABEL_KEY: Record<EndorsementKind, LocalizationKey> = {
    TAUGHT_ME: "discussion.endorsement.kind.taught_me",
    FAIR_TO_THE_OTHER_SIDE: "discussion.endorsement.kind.fair_to_the_other_side",
    WELL_SAID: "discussion.endorsement.kind.well_said",
    MOVED_ME: "discussion.endorsement.kind.moved_me",
    STAYED_WITH_ME: "discussion.endorsement.kind.stayed_with_me",
    KEPT_ME_READING: "discussion.endorsement.kind.kept_me_reading",
    PUT_IT_INTO_WORDS: "discussion.endorsement.kind.put_it_into_words",
};

// One icon per kind, and every one of them a metaphor for the READING, never for the writer: a flame
// or a trophy would be about the person and §8.2 rules that out. `Waves` for STAYED_WITH_ME is the
// afterimage; `Quote` for PUT_IT_INTO_WORDS is the reader finding their own sentence in someone
// else's paragraph.
const KIND_ICON: Record<EndorsementKind, LucideIcon> = {
    TAUGHT_ME: Lightbulb,
    FAIR_TO_THE_OTHER_SIDE: Scale,
    WELL_SAID: Feather,
    MOVED_ME: Heart,
    STAYED_WITH_ME: Waves,
    KEPT_ME_READING: BookOpen,
    PUT_IT_INTO_WORDS: Quote,
};

/**
 * How many kind names the chip spells out inline before collapsing the rest into `+N`.
 *
 * Two was three kinds' natural ceiling and is seven kinds' necessary one. `ThoughtButtons` states the
 * invariant in its own comment — "nothing else in the row is allowed to wrap on its own" — and with a
 * seven-kind vocabulary a well-received poem can genuinely collect seven distinct kinds, which at
 * Spanish and Portuguese label lengths is far past the row's width. Nothing is lost: `chipTitle`
 * already carries every kind and is bound to both `title` and `aria-label`, so the full list stays
 * available to a pointer and to a screen reader.
 */
const CHIP_INLINE_KIND_LIMIT = 2;

/**
 * How long a first plain endorsement can be taken back (QoL work order D6, E11 amendment). A thumb slip
 * deserves a way back; a considered retraction does not exist in v1, so the window is the toast's life.
 */
export const ENDORSEMENT_UNDO_WINDOW_MS = 10_000;

const REFUSAL_COPY_KEY: Record<EndorsementRefusalCode, LocalizationKey> = {
    thought_not_found: "discussion.endorsement.error.generic",
    own_thought: "discussion.endorsement.error.own_thought",
    blocked: "discussion.endorsement.error.blocked",
    account_restricted: "discussion.endorsement.error.blocked",
    kind_required: "discussion.endorsement.error.generic",
    note_too_long: "discussion.endorsement.error.note_too_long",
    note_refused: "discussion.endorsement.error.note_refused",
    not_endorsed: "discussion.endorsement.error.generic",
};

export function refusalCopyKey(error: unknown): LocalizationKey {
    const code = readEndorsementRefusalCode(error);
    return code ? REFUSAL_COPY_KEY[code] : "discussion.endorsement.error.generic";
}

/**
 * The endorsement control, the chip and the note composer for ONE thought — one component, one source.
 *
 * <p><b>Why one component owns all three.</b> `ThoughtButtons` is rendered by three hosts (the
 * opening-thought card, the reply tree, the conversation view), and the mind-changed precedent —
 * the CARD owning the flag and the row reporting clicks — works only because that control is
 * author-only and lives on one host. An endorsement is offered on every thought a reader is not the
 * author of, on every host, so the state lives here and every host gets it for free. The value the
 * control reads and the value the chip draws are the same record, so they cannot drift (rule 31's
 * "delete one of the copies", applied before the second copy exists).
 *
 * <p><b>The record is the `"thought-endorsement"` viewer-action record</b> (rule 33's Back-restoration
 * cache serves stale DTOs, and a member who just endorsed must see it on Back). It is written only from
 * the server's own summary, never from an optimistic guess: a refusal — self, blocked, restricted, a
 * held note — is a legitimate outcome and the chip must not show a count the server then takes back.
 *
 * <p><b>Rendering.</b> Hidden entirely on the viewer's own thought and on a tombstone. The control is
 * an outline `sm` pill, quieter than Reply and never its peer (design §9.1). Once the viewer has
 * endorsed, the same pill reads their kind and is `aria-pressed`; its menu offers the other two kinds
 * (E3, changeable) and "Add a note" while there is none. There is deliberately NO un-endorse item (E11;
 * `.agents/questions/w2-endorsements-1.md`); the only way back is the 10 s Undo on a first plain
 * endorsement's toast (E11 amendment, QoL D6). The chip renders whenever the server reports a live count
 * and is `null` when the DTO carried no summary at all, which is how the reply tree renders today.
 *
 * <p>Design: `designs/00-inbox/2026-08-25-profile-endorsements-and-the-appreciation-wall-design.md`
 * §7, §8, §9.1, §9.4, §7.15.
 */
export function EndorsementControl(props: {
    thoughtPublicId: string;
    localizer: Localizer;
    /** The DTO's summary, or null/undefined when the projection did not resolve it. */
    initialSummary?: ThoughtEndorsementSummary | null;
    accountAccessDialog: ContextualAccountAccessDialogContext;
    /** Rendered as `null` on the viewer's own thought and on a tombstone. */
    hidden?: boolean;
}) {
    const { thoughtPublicId, localizer, initialSummary, accountAccessDialog, hidden } = props;

    // Memoized on CONTENT so the hook's resync effect sees a stable initial value across renders while
    // a genuinely new DTO still re-seeds an untouched control.
    const initialRecord = useMemo(
        () => toEndorsementRecord(initialSummary),
        [
            initialSummary?.count,
            initialSummary?.viewerKind,
            initialSummary?.viewerHasNote,
            (initialSummary?.kinds ?? []).join(","),
        ],
    );
    const [record, setRecord] = useViewerActionRecord("thought-endorsement", thoughtPublicId, initialRecord);
    const [submitting, setSubmitting] = useState(false);
    const [composerKind, setComposerKind] = useState<EndorsementKind | null>(null);
    // The live Undo toast, if any. Adding a note dismisses it: a note endorsement is never undone (D6).
    const undoToastIdRef = useRef<string | number | null>(null);

    const viewerKind = record?.viewerKind ?? null;
    const kinds = kindsOfRecord(record);

    const applyServerSummary = (summary: ThoughtEndorsementSummary | null | undefined): ThoughtEndorsementRecord | null => {
        const next = toEndorsementRecord(summary);
        if (next) {
            setRecord(next);
        }
        return next;
    };

    const submit = async (kind: EndorsementKind, note?: string): Promise<ThoughtEndorsementRecord | null> => {
        if (submitting) {
            return null;
        }
        setSubmitting(true);
        try {
            const response = await ClientFactory.endorseThought(thoughtPublicId, note ? { kind, note } : { kind });
            return applyServerSummary(response.data);
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return null;
            }
            // A 4xx here is a designed refusal; the code picks the sentence.
            logger.warn(e);
            toast.error(localizer(refusalCopyKey(e)));
            return null;
        } finally {
            setSubmitting(false);
        }
    };

    const chooseKind = async (kind: EndorsementKind) => {
        const previousKind = viewerKind;
        const next = await submit(kind);
        if (!next) {
            return;
        }
        if (previousKind !== kind) {
            // D6: Undo only for a FIRST plain endorsement. A kind change is not offered one, because the
            // DELETE removes the whole endorsement rather than restoring the previous kind.
            const undoable = previousKind === null && !next.viewerHasNote;
            const toastId = toast.success(localizer("discussion.endorsement.toast.saved"), {
                duration: undoable ? ENDORSEMENT_UNDO_WINDOW_MS : undefined,
                action: undoable
                    ? {
                        // The stable hook rides inside the label (the unsave-undo precedent).
                        label: <span data-playwright-id="thought-endorsement-undo-action">{localizer("literal.undo")}</span>,
                        onClick: () => { void undoEndorsement(); },
                    }
                    : undefined,
            });
            undoToastIdRef.current = undoable ? toastId : null;
        }
        // The act is complete. The sentence is offered once, kind-scoped, and Skip is free (E4).
        if (!next.viewerHasNote) {
            setComposerKind(kind);
        }
    };

    const undoEndorsement = async () => {
        undoToastIdRef.current = null;
        setComposerKind(null);
        try {
            const response = await ClientFactory.removeThoughtEndorsement(thoughtPublicId);
            applyServerSummary(response.data);
            toast.success(localizer("discussion.endorsement.toast.undone"));
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.warn(e);
            toast.error(localizer(refusalCopyKey(e)));
        }
    };

    const submitNote = async (note: string) => {
        const kind = composerKind ?? viewerKind;
        if (!kind) {
            setComposerKind(null);
            return;
        }
        const next = await submit(kind, note);
        if (next) {
            setComposerKind(null);
            if (undoToastIdRef.current !== null) {
                toast.dismiss(undoToastIdRef.current);
                undoToastIdRef.current = null;
            }
            toast.success(localizer("discussion.endorsement.toast.note_saved"));
        }
    };

    if (hidden) {
        return null;
    }

    const kindItems: DropdownItem[] = ENDORSEMENT_KINDS
        .filter((kind) => kind !== viewerKind)
        .map((kind) => ({
            id: `endorse-${kind.toLowerCase()}`,
            icon: KIND_ICON[kind],
            text: localizer(ENDORSEMENT_KIND_LABEL_KEY[kind]),
            requiresLogin: false as const,
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void chooseKind(kind);
            },
        }));
    if (viewerKind && !record?.viewerHasNote) {
        kindItems.push({
            id: "endorse-add-note",
            icon: MessageSquarePlus,
            text: localizer("discussion.endorsement.note.add"),
            requiresLogin: false as const,
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                setComposerKind(viewerKind);
            },
        });
    }

    const triggerText = viewerKind
        ? localizer(ENDORSEMENT_KIND_LABEL_KEY[viewerKind])
        : localizer("discussion.endorsement.action");
    const chipKindLabels = kinds.map((kind) => localizer(ENDORSEMENT_KIND_LABEL_KEY[kind]));
    // The chip shows the first two and counts the rest; the title below still names them all. `+N` is
    // digits and a plus, so it needs no catalog key and reads the same in all 25 locales.
    const chipOverflowCount = Math.max(0, chipKindLabels.length - CHIP_INLINE_KIND_LIMIT);
    const chipKindsText = [
        ...chipKindLabels.slice(0, CHIP_INLINE_KIND_LIMIT),
        ...(chipOverflowCount > 0 ? [`+${chipOverflowCount}`] : []),
    ].join(" · ");
    const chipAllKindsText = chipKindLabels.join(" · ");
    const chipTitle = `${localizer("discussion.endorsement.chip.label")}: ${record?.count ?? 0}`
        + (chipAllKindsText ? ` · ${chipAllKindsText}` : "");

    return (
        <>
            <span
                className="inline-flex items-center gap-2"
                data-playwright-id="thought-endorsement"
                data-thought-public-id={thoughtPublicId}
                // Written in BOTH states, so an absence assertion can tell "not endorsed" from "not
                // mounted" (rule 20: assert the effect through a stable hook).
                data-viewer-endorsement-kind={viewerKind ?? ""}
            >
                <CtaButtonDropdown
                    icon={viewerKind ? KIND_ICON[viewerKind] : Diamond}
                    text={triggerText}
                    ariaLabel={viewerKind
                        ? `${localizer("discussion.endorsement.action")}: ${triggerText}`
                        : localizer("discussion.endorsement.action")}
                    variant="outline"
                    size="sm"
                    // DISCUSSION-46. Every sibling in a thought's action row passes
                    // `displayTextOnMobile={false}` (six occurrences in ThoughtButtons.tsx); this call
                    // site did not, and BOTH CtaButtonDropdown and CtaButtonContent default it to true.
                    // So on a phone Endorse was the only control in the row still wearing a word, which
                    // inverted the row's own stated hierarchy (ThoughtButtons: "a quiet secondary
                    // control, never Reply's peer") and spent ~46 CSS px that made the row wrap at every
                    // nesting depth — against the invariant ThoughtButtons states in its own comment,
                    // "nothing else in the row is allowed to wrap on its own".
                    //
                    // Nothing is lost when the word goes: `ariaLabel` above already carries the whole
                    // meaning, naming the action and the viewer's current kind. Desktop is untouched —
                    // the prop only gates the label's `hidden sm:inline`.
                    //
                    // The primitive's default is the real root, but CtaButtonDropdown.tsx is frozen
                    // pending wave-19 Q6, so this is the call-site fix §2.3 of the pass plan prescribes.
                    displayTextOnMobile={false}
                    extraClass="endorse-thought-button"
                    playwrightId="thought-endorse-button"
                    buttonActive={Boolean(viewerKind)}
                    activeClass="border-primary/60 bg-primary/10 text-primary"
                    requiresLogin={true}
                    accountAccessDialog={accountAccessDialog}
                    isLoading={submitting}
                    dropdownItems={kindItems}
                />
                {record && record.count > 0 ? (
                    <span
                        className="inline-flex min-h-8 items-center gap-1 rounded-full border border-border bg-card px-2.5 text-xs text-neutral-text"
                        title={chipTitle}
                        aria-label={chipTitle}
                        data-playwright-id="thought-endorsement-chip"
                        data-endorsement-count={record.count}
                        data-endorsement-kinds={record.kindsCsv}
                    >
                        <Diamond className="size-3" aria-hidden="true" />
                        <span className="tabular-nums">{record.count > 99 ? "99+" : record.count}</span>
                        {chipKindsText ? (
                            <span className="hidden sm:inline" aria-hidden="true">· {chipKindsText}</span>
                        ) : null}
                    </span>
                ) : null}
            </span>
            {composerKind ? (
                <div className="basis-full" data-playwright-id="thought-endorsement-composer-slot">
                    <EndorsementComposer
                        localizer={localizer}
                        kind={composerKind}
                        submitting={submitting}
                        onSubmit={(note) => void submitNote(note)}
                        onSkip={() => setComposerKind(null)}
                    />
                </div>
            ) : null}
        </>
    );
}

export default EndorsementControl;
